"use client";

import { Crown, Megaphone, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ListingPromotion } from "@/types/listing";

/**
 * Card container classes for a highlighted listing: accent ring + tinted
 * background, so the card itself stands out (the badge carries the meaning in
 * text, so this is never colour-only).
 *
 * `surface` says what the card's text colours assume in dark mode:
 * - "adaptive": the card has dark-mode text colours, so a dark tint is safe.
 * - "light": the card stays light in dark mode, so the tint must stay light.
 */
export function highlightedCardClasses(surface: "adaptive" | "light"): string {
  return surface === "adaptive"
    ? "ring-2 ring-purple-500/70 dark:ring-purple-400/70 bg-purple-50 dark:bg-purple-950/40"
    : "ring-2 ring-purple-500/70 dark:ring-purple-400/80 border-purple-300 bg-purple-50";
}

interface PromotionBadgesProps {
  promotion?: ListingPromotion | null;
  /** Corner radius / padding to match the host card's other pills. */
  pillClassName?: string;
}

/**
 * Paid-promotion pills for a listing card. Keyed off the API's resolved
 * `promotion` flags only:
 * - featured → "Featured" (crown)
 * - highlighted → "Highlighted" (the card styling is the host's job)
 * - sponsored → "Promoted", so people know why a listing that may not match
 *   their filters is in the results
 * - relevant → nothing; its benefit is ranking
 */
export default function PromotionBadges({
  promotion,
  pillClassName = "px-2.5 py-0.5 rounded",
}: PromotionBadgesProps) {
  const { t } = useTranslation("propertyCard");
  if (!promotion) return null;
  const base = `inline-flex items-center gap-1 backdrop-blur-sm text-white text-xs font-medium shadow-sm ${pillClassName}`;

  return (
    <>
      {promotion.sponsored && (
        <span
          className={`${base} bg-gray-900/80`}
          title={t("promotedHint")}
          aria-label={t("promotedHint")}
          data-testid="badge-sponsored"
        >
          <Megaphone className="w-3 h-3" aria-hidden="true" />
          {t("promoted")}
        </span>
      )}
      {promotion.featured && (
        <span className={`${base} bg-amber-500/90`} data-testid="badge-featured">
          <Crown className="w-3.5 h-3.5" aria-hidden="true" />
          {t("featured")}
        </span>
      )}
      {promotion.highlighted && (
        <span className={`${base} bg-purple-600/90`} data-testid="badge-highlighted">
          <Sparkles className="w-3 h-3" aria-hidden="true" />
          {t("highlighted")}
        </span>
      )}
    </>
  );
}
